// Headless driver orchestration (fn-69.2, R1/R4).
//
// Pure logic over a `HeadlessTransport`, with injectable `sleep`/`now`
// so the bounded waits are deterministic in unit tests. Three pieces:
//
//   1. `bootstrapAndKickoff` — create the session, then send the kickoff
//      immediately (R1). fn-72 removed the separate fn-67 `<system-bootstrap>`
//      turn: a freshly created session is `pending` (sendable) and grounding
//      now rides the first user message (delivered server-side), so there is
//      NO bootstrap-ack to wait for, NO idle poll, NO sendable-status race,
//      and NO 409-retry. A conflict on the send is unexpected and surfaces as
//      "bootstrap-unavailable" (defensive); the wait/poll/timeout machinery is
//      gone.
//   2. `resolvePendingApproval` — the approval-row resolver (R4). PRIMARY
//      path: poll `approvals.list`, filter by `session_id` (+ optional
//      bare tool name), with bounded retry for webhook-insert lag. The
//      list returns `id`/`tool_name`/`tool_args`/`ack_kind` directly — no
//      event correlation, so it sidesteps the idle-vs-tool-use
//      enrichment ambiguity entirely.
//   3. `classifyEvent` — turn one `/events` envelope into typed view-
//      events (status/prose/tool/gate/tokens) for the UI (fn-69.3).

import { type ApprovalMode, bareToolName } from './approval-policy.js';
import { parseStatusBlock } from './parse-status.js';
import { type HeadlessStatus, NEUTRAL_FALLBACK_STATUS } from './status-contract.js';
import type {
  HeadlessApprovalRow,
  HeadlessCreateInput,
  HeadlessRunEvent,
  HeadlessTransport,
} from './transport.js';

const DEFAULT_POLL_INTERVAL_MS = 1_500;
const DEFAULT_APPROVAL_MAX_ATTEMPTS = 8;

const defaultSleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

interface ClockDeps {
  readonly sleep?: (ms: number) => Promise<void>;
  readonly now?: () => number;
}

// ── 1. Bootstrap + kickoff ───────────────────────────────────────────

export interface BootstrapAndKickoffInput {
  readonly transport: HeadlessTransport;
  readonly create: HeadlessCreateInput;
  readonly kickoff: string;
}

export type BootstrapAndKickoffResult =
  | {
      readonly outcome: 'kickoff-sent';
      readonly sessionId: string;
    }
  | {
      readonly outcome: 'bootstrap-unavailable';
      readonly sessionId: string;
      /** The non-sendable status surfaced if the (unexpected) send conflicts. */
      readonly status: string;
    };

/**
 * Best-effort detection of a 409/conflict thrown by `sendMessage`. The CP
 * message route returns `409` when the session is no longer in a sendable
 * status; the BFF tRPC client surfaces that as a `CONFLICT` error carrying
 * `httpStatus: 409`. We narrow defensively across the shapes the client
 * can throw (and the raw message) so a status→send race degrades to
 * "bootstrap-unavailable" instead of an unhandled throw.
 */
function isConflictError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') {
    return false;
  }
  const e = err as {
    data?: { httpStatus?: unknown; code?: unknown };
    shape?: { data?: { httpStatus?: unknown; code?: unknown } };
    message?: unknown;
  };
  if (e.data?.httpStatus === 409 || e.shape?.data?.httpStatus === 409) {
    return true;
  }
  if (e.data?.code === 'CONFLICT' || e.shape?.data?.code === 'CONFLICT') {
    return true;
  }
  return typeof e.message === 'string' && /\b409\b|conflict/i.test(e.message);
}

/**
 * Create the session, then send the kickoff — immediately. fn-72 removed the
 * separate fn-67 `<system-bootstrap>` turn: a freshly created session is
 * `pending` (sendable) and grounding now rides the first user message
 * (delivered server-side). So there is NO bootstrap-ack to wait for, NO idle
 * poll, NO sendable-status race, and NO 409-retry — the create→send→stream flow
 * is MA's native two-step lifecycle. The transient `409 session_busy` that drove
 * the old bootstrap-wait dance cannot occur here anymore (the row never leaves
 * `pending` between create and the first send).
 *
 * A conflict on the send is therefore unexpected; we stay defensive and surface
 * it as `bootstrap-unavailable` (re-reading the live status) rather than letting
 * an unhandled 409 escape — but the wait/poll/timeout machinery is gone.
 */
export async function bootstrapAndKickoff(
  input: BootstrapAndKickoffInput,
): Promise<BootstrapAndKickoffResult> {
  const { sessionId } = await input.transport.createSession(input.create);
  try {
    await input.transport.sendMessage(sessionId, input.kickoff);
  } catch (err) {
    if (isConflictError(err)) {
      const status = await input.transport.getSessionStatus(sessionId).catch(() => 'unknown');
      return { outcome: 'bootstrap-unavailable', sessionId, status };
    }
    throw err;
  }
  return { outcome: 'kickoff-sent', sessionId };
}

// ── 2. Approval-row resolver ─────────────────────────────────────────

export interface ResolvePendingApprovalInput extends ClockDeps {
  readonly transport: HeadlessTransport;
  readonly sessionId: string;
  /** Optional bare-name filter (e.g. `review_matter`). */
  readonly toolName?: string;
  /** Approval ids already handled this run — skip them. */
  readonly excludeApprovalIds?: ReadonlySet<string>;
  /** Bounded retries to tolerate webhook-insert lag. Default 8. */
  readonly maxAttempts?: number;
  readonly pollIntervalMs?: number;
}

/**
 * Resolve the pending approval row for a gate by polling
 * `approvals.list` and filtering by `session_id` (+ optional bare tool
 * name), with bounded retry. Returns the first matching row or `null`.
 */
export async function resolvePendingApproval(
  input: ResolvePendingApprovalInput,
): Promise<HeadlessApprovalRow | null> {
  const sleep = input.sleep ?? defaultSleep;
  const maxAttempts = input.maxAttempts ?? DEFAULT_APPROVAL_MAX_ATTEMPTS;
  const pollMs = input.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const wantBare = input.toolName !== undefined ? bareToolName(input.toolName) : undefined;
  const exclude = input.excludeApprovalIds ?? new Set<string>();

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const rows = await input.transport.listApprovals();
    const match = rows.find(
      (r) =>
        r.session_id === input.sessionId &&
        !exclude.has(r.id) &&
        (wantBare === undefined || bareToolName(r.tool_name) === wantBare),
    );
    if (match !== undefined) {
      return match;
    }
    if (attempt < maxAttempts - 1) {
      await sleep(pollMs);
    }
  }
  return null;
}

// ── 3. Event classifier ──────────────────────────────────────────────

export type DriverViewEvent =
  | { readonly kind: 'status'; readonly status: HeadlessStatus }
  | { readonly kind: 'prose'; readonly text: string }
  | { readonly kind: 'fallback'; readonly status: HeadlessStatus; readonly reason: string }
  | {
      readonly kind: 'tool';
      readonly tool: string;
      readonly toolKind: 'builtin' | 'mcp' | 'custom';
    }
  | { readonly kind: 'gate'; readonly stopReason: string }
  | { readonly kind: 'idle' }
  | { readonly kind: 'tokens'; readonly usage: Record<string, unknown> };

function textBlocks(data: Record<string, unknown>): string[] {
  const content = (data as { content?: unknown }).content;
  if (typeof content === 'string') {
    return [content];
  }
  if (!Array.isArray(content)) {
    return [];
  }
  const out: string[] = [];
  for (const block of content) {
    if (typeof block === 'string') {
      out.push(block);
    } else if (block !== null && typeof block === 'object') {
      const text = (block as { text?: unknown }).text;
      if (typeof text === 'string') {
        out.push(text);
      }
    }
  }
  return out;
}

/**
 * Classify one `/events` envelope into zero-or-more typed view events.
 *
 *   - `agent.message` → in `headless` mode each text block is parsed to a
 *     `status` (or a `fallback` carrying a NEUTRAL status — never the raw
 *     text); in `chat` mode each block is `prose`.
 *   - `agent.mcp_tool_use` / `agent.custom_tool_use` / `agent.tool_use`
 *     → a `tool` activity event (bare tool name).
 *   - `session.status_idle` → `gate` (requires_action) or `idle`.
 *   - `span.model_request_end` → `tokens` (usage passthrough).
 *   - everything else → no view events.
 */
export function classifyEvent(
  event: HeadlessRunEvent,
  mode: 'headless' | 'chat',
): DriverViewEvent[] {
  switch (event.type) {
    case 'agent.message': {
      const out: DriverViewEvent[] = [];
      for (const text of textBlocks(event.data)) {
        if (text.trim().length === 0) {
          continue;
        }
        if (mode === 'chat') {
          out.push({ kind: 'prose', text });
          continue;
        }
        const parsed = parseStatusBlock(text);
        if (parsed.outcome === 'status') {
          out.push({ kind: 'status', status: parsed.status });
        } else {
          out.push({ kind: 'fallback', status: NEUTRAL_FALLBACK_STATUS, reason: parsed.reason });
        }
      }
      return out;
    }
    case 'agent.mcp_tool_use':
      return [{ kind: 'tool', tool: bareToolName(toolName(event.data)), toolKind: 'mcp' }];
    case 'agent.custom_tool_use':
      return [{ kind: 'tool', tool: bareToolName(toolName(event.data)), toolKind: 'custom' }];
    case 'agent.tool_use':
      return [{ kind: 'tool', tool: bareToolName(toolName(event.data)), toolKind: 'builtin' }];
    case 'session.status_idle': {
      const stopReason = (event.data as { stop_reason?: { type?: unknown } }).stop_reason?.type;
      if (stopReason === 'requires_action') {
        return [{ kind: 'gate', stopReason: 'requires_action' }];
      }
      return [{ kind: 'idle' }];
    }
    case 'span.model_request_end': {
      const usage = (event.data as { usage?: unknown }).usage;
      if (usage !== null && typeof usage === 'object') {
        return [{ kind: 'tokens', usage: usage as Record<string, unknown> }];
      }
      return [];
    }
    default:
      return [];
  }
}

function toolName(data: Record<string, unknown>): string {
  const name = (data as { name?: unknown }).name;
  return typeof name === 'string' ? name : '';
}

export type { ApprovalMode };
